import { createServerFn } from "@tanstack/react-start";
import { BUSINESS } from "./constants";

export interface QuoteFormData {
  name: string;
  phone: string;
  email: string;
  service?: string;
  propertyType?: string;
  address?: string;
  message?: string;
  _hp?: string;
}

export interface ContactFormData {
  name: string;
  email: string;
  phone?: string;
  inquiryType?: string;
  message: string;
  _hp?: string;
}

/**
 * Read a server-side env var at request time.
 *
 * Works in both runtimes we care about:
 * - `pnpm dev`, where the dev server loads `.env` into process.env.
 * - Cloudflare Workers, where secrets and vars are populated onto process.env
 *   automatically (nodejs_compat + a compatibility_date past 2025-04-01; ours
 *   is 2026-03-03, so this holds).
 *
 * Never import this into client code. These values must not reach the bundle.
 */
function env(name: string): string | undefined {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const v = (globalThis as any)?.process?.env?.[name];
    return typeof v === "string" && v.trim() ? v.trim() : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Where mail goes and who it comes from.
 *
 * RESEND_TO overrides the recipient. Unset, mail goes to `BUSINESS.email`
 * (info@bluerockconcreteco.com). Note that verifying a domain in Resend enables
 * SENDING from it; it does not create a mailbox that can RECEIVE. If that
 * address has no inbox behind it yet, set RESEND_TO to one that does.
 *
 * RESEND_FROM sets the sender. The fallback is Resend's shared
 * `onboarding@resend.dev`, which may ONLY deliver to the address the Resend
 * account was registered with, and returns 403 for anyone else. Now that
 * bluerockconcreteco.com is verified, set RESEND_FROM to an address on it: that
 * lifts the restriction and stops mail looking like it came from a stranger.
 */
function mailConfig() {
  return {
    apiKey: env("RESEND_API_KEY"),
    from: env("RESEND_FROM") ?? "Blue Rock Concrete <onboarding@resend.dev>",
    to: env("RESEND_TO") ?? BUSINESS.email,
  };
}

const SITE = "https://bluerockconcreteco.com";

/** Escape user-supplied values before they go anywhere near the HTML email. */
function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Branded HTML email for an inbound lead.
 *
 * Written to email-client rules, not web rules: table layout, inline styles
 * only, no flexbox or grid, 600px max width, web-safe font stack (Archivo is
 * not available in Gmail or Outlook). The logo is an absolute URL on the live
 * site because mail clients cannot resolve relative paths.
 *
 * The phone and email values render as tel:/mailto: links so the lead is
 * actionable in one tap from a phone.
 */
function renderLeadEmail(opts: {
  heading: string;
  intro: string;
  rows: Array<{ label: string; value: string; href?: string }>;
  message?: { label: string; value: string };
}): string {
  const row = (r: { label: string; value: string; href?: string }) => `
    <tr>
      <td style="padding:14px 0;border-bottom:1px solid #eeeeee;">
        <div style="font:700 11px/1.2 Arial,Helvetica,sans-serif;letter-spacing:1.5px;text-transform:uppercase;color:#8a8a8a;">${esc(r.label)}</div>
        <div style="margin-top:5px;font:600 16px/1.4 Arial,Helvetica,sans-serif;color:#000000;">${
          r.href
            ? `<a href="${esc(r.href)}" style="color:#F35122;text-decoration:none;">${esc(r.value)}</a>`
            : esc(r.value)
        }</div>
      </td>
    </tr>`;

  return `<!doctype html>
<html><body style="margin:0;padding:0;background:#f4f5f7;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#f4f5f7;padding:24px 12px;">
    <tr><td align="center">
      <table role="presentation" width="600" cellpadding="0" cellspacing="0" style="width:600px;max-width:100%;background:#ffffff;border:1px solid #e3e3e3;">

        <tr><td style="background:#000000;padding:22px 28px;">
          <img src="${SITE}/blue-rock-logo-flame.png" width="150" alt="Blue Rock Concrete"
               style="display:block;border:0;width:150px;height:auto;">
        </td></tr>
        <tr><td style="height:5px;background:#F35122;font-size:0;line-height:0;">&nbsp;</td></tr>

        <tr><td style="padding:30px 28px 6px;">
          <h1 style="margin:0;font:800 24px/1.2 Arial,Helvetica,sans-serif;color:#000000;text-transform:uppercase;letter-spacing:-0.4px;">${esc(opts.heading)}</h1>
          <p style="margin:10px 0 0;font:400 15px/1.55 Arial,Helvetica,sans-serif;color:#5c5c5c;">${esc(opts.intro)}</p>
        </td></tr>

        <tr><td style="padding:10px 28px 0;">
          <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
            ${opts.rows.filter((r) => r.value).map(row).join("")}
          </table>
        </td></tr>

        ${
          opts.message?.value
            ? `<tr><td style="padding:22px 28px 0;">
                 <div style="font:700 11px/1.2 Arial,Helvetica,sans-serif;letter-spacing:1.5px;text-transform:uppercase;color:#8a8a8a;">${esc(opts.message.label)}</div>
                 <div style="margin-top:8px;padding:16px;background:#f4f5f7;border-left:4px solid #F35122;font:400 15px/1.6 Arial,Helvetica,sans-serif;color:#1a1a1a;white-space:pre-wrap;">${esc(opts.message.value)}</div>
               </td></tr>`
            : ""
        }

        <tr><td style="padding:26px 28px 30px;">
          <table role="presentation" cellpadding="0" cellspacing="0"><tr>
            <td style="background:#F35122;">
              <a href="tel:${esc(BUSINESS.phoneHref.replace("tel:", ""))}"
                 style="display:inline-block;padding:14px 26px;font:700 14px/1 Arial,Helvetica,sans-serif;color:#ffffff;text-decoration:none;text-transform:uppercase;letter-spacing:0.5px;">
                Call ${esc(BUSINESS.phone)}
              </a>
            </td>
          </tr></table>
          <p style="margin:16px 0 0;font:400 13px/1.5 Arial,Helvetica,sans-serif;color:#8a8a8a;">
            Hit reply to answer this lead directly. Your reply goes straight to them.
          </p>
        </td></tr>

        <tr><td style="background:#000000;padding:18px 28px;">
          <p style="margin:0;font:400 12px/1.5 Arial,Helvetica,sans-serif;color:#9a9a9a;">
            Sent from <a href="${SITE}" style="color:#FF7A50;text-decoration:none;">bluerockconcreteco.com</a>
          </p>
        </td></tr>

      </table>
    </td></tr>
  </table>
</body></html>`;
}

/**
 * One place that talks to Resend, shared by both forms.
 *
 * Sends multipart: `html` for the branded version, `text` as the fallback for
 * plain-text clients. Including both also helps deliverability, since
 * HTML-only mail scores worse with spam filters.
 *
 * On failure it logs Resend's actual response body server-side. The previous
 * version discarded it and returned a generic string, which made a misconfigured
 * key or a blocked recipient impossible to diagnose from the outside.
 */
async function sendViaResend(opts: {
  subject: string;
  text: string;
  html?: string;
  replyTo: string;
  label: string;
}): Promise<{ success: true } | { success: false; error: string }> {
  const { apiKey, from, to } = mailConfig();

  if (!apiKey) {
    console.warn(
      `[Blue Rock Concrete] ${opts.label}: RESEND_API_KEY is not set, so nothing was emailed. ` +
        `The form still reported success to the visitor.\n${opts.text}`,
    );
    return { success: true };
  }

  try {
    const res = await fetch("https://api.resend.com/emails", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${apiKey}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        from,
        to: [to],
        reply_to: opts.replyTo,
        subject: opts.subject,
        text: opts.text,
        ...(opts.html ? { html: opts.html } : {}),
      }),
    });

    if (!res.ok) {
      const detail = await res.text().catch(() => "<no body>");
      console.error(
        `[Blue Rock Concrete] ${opts.label}: Resend rejected the send.\n` +
          `  status: ${res.status}\n  from: ${from}\n  to: ${to}\n  response: ${detail}`,
      );
      return {
        success: false,
        error: `Could not send right now. Please call us at ${BUSINESS.phone}.`,
      };
    }

    return { success: true };
  } catch (err) {
    console.error(`[Blue Rock Concrete] ${opts.label}: network error calling Resend.`, err);
    return { success: false, error: `An error occurred. Please call ${BUSINESS.phone}.` };
  }
}

/**
 * Estimate-request handler.
 * - Drops silently on honeypot hits.
 * - Sends via the Resend HTTP API when RESEND_API_KEY is set (edge-friendly fetch).
 * - Without a key (dev / pre-launch) it logs and returns success so the form is testable.
 */
export const sendQuoteEmailAction = createServerFn({ method: "POST" })
  .validator((data: QuoteFormData) => data)
  .handler(async ({ data }) => {
    if (data._hp) return { success: true as const };

    if (!data.name?.trim() || !data.phone?.trim() || !data.email?.trim()) {
      return { success: false as const, error: "Please fill in your name, phone, and email." };
    }

    const summary = [
      `Name: ${data.name}`,
      `Phone: ${data.phone}`,
      `Email: ${data.email}`,
      data.service ? `Service: ${data.service}` : null,
      data.propertyType ? `Property: ${data.propertyType}` : null,
      data.address ? `Address: ${data.address}` : null,
      data.message ? `Details: ${data.message}` : null,
    ]
      .filter(Boolean)
      .join("\n");

    return sendViaResend({
      label: "Estimate request",
      subject: `New estimate request — ${data.name}`,
      text: summary,
      html: renderLeadEmail({
        heading: "New estimate request",
        intro: "Someone asked for a free estimate through the website.",
        rows: [
          { label: "Name", value: data.name },
          { label: "Phone", value: data.phone, href: `tel:${data.phone.replace(/[^\d+]/g, "")}` },
          { label: "Email", value: data.email, href: `mailto:${data.email}` },
          { label: "Service", value: data.service ?? "" },
          { label: "Property type", value: data.propertyType ?? "" },
          { label: "Project address", value: data.address ?? "" },
        ],
        message: { label: "Project details", value: data.message ?? "" },
      }),
      replyTo: data.email,
    });
  });

/**
 * General-inquiry handler for /contact. Same shape as `sendQuoteEmailAction`:
 * drops silently on honeypot hits, requires name + email + message, dev-logs
 * and returns success without a key, otherwise POSTs to Resend.
 */
export const sendContactEmailAction = createServerFn({ method: "POST" })
  .validator((data: ContactFormData) => data)
  .handler(async ({ data }) => {
    if (data._hp) return { success: true as const };

    if (!data.name?.trim() || !data.email?.trim() || !data.message?.trim()) {
      return { success: false as const, error: "Please fill in your name, email, and message." };
    }

    const summary = [
      `Name: ${data.name}`,
      `Email: ${data.email}`,
      data.phone ? `Phone: ${data.phone}` : null,
      `Reason: ${data.inquiryType || "General"}`,
      `Message: ${data.message}`,
    ]
      .filter(Boolean)
      .join("\n");

    return sendViaResend({
      label: "Contact inquiry",
      // Em dashes here are fine: this is a server-side email subject, not visible site copy.
      subject: `Contact Inquiry — ${data.inquiryType || "General"} — ${data.name}`,
      text: summary,
      html: renderLeadEmail({
        heading: "New contact inquiry",
        intro: "Someone sent a message through the website contact form.",
        rows: [
          { label: "Name", value: data.name },
          { label: "Email", value: data.email, href: `mailto:${data.email}` },
          ...(data.phone
            ? [{ label: "Phone", value: data.phone, href: `tel:${data.phone.replace(/[^\d+]/g, "")}` }]
            : []),
          { label: "Reason", value: data.inquiryType || "General" },
        ],
        message: { label: "Message", value: data.message },
      }),
      replyTo: data.email,
    });
  });
